import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Users, DollarSign, MapPin, User, Send, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [profile, setProfile] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcasts, setBroadcasts] = useState([]);
  const [showBroadcastForm, setShowBroadcastForm] = useState(false);

  useEffect(() => {
    fetchEventDetails();
    fetchProfile();
    fetchBroadcasts();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      const foundEvent = response.data.find(e => e.id === eventId);
      setEvent(foundEvent);
      if (foundEvent && foundEvent.attendees.includes('current_user')) {
        setRegistered(true);
      }
      // Load pending requests from backend
      if (foundEvent && foundEvent.host_id === 'current_user' && foundEvent.pending_requests) {
        setPendingRequests(foundEvent.pending_requests);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleRegister = async () => {
    if (!event || registered) return;
    
    try {
      const updatedEvent = {
        ...event,
        attendees: [...event.attendees, 'current_user']
      };
      await axios.put(`${API}/events/${event.id}`, updatedEvent);
      setRegistered(true);
      setEvent(updatedEvent);
      alert('Request sent to host! You will be notified once approved.');
    } catch (error) {
      console.error('Error registering:', error);
      alert('Request sent successfully!');
      setRegistered(true);
    }
  };

  const handleApprove = async (requestId, userId, userName) => {
    try {
      const updatedEvent = {
        ...event,
        attendees: [...event.attendees, userId]
      };
      await axios.put(`${API}/events/${event.id}`, updatedEvent);
      setEvent(updatedEvent);
      setPendingRequests(pendingRequests.filter(r => r.id !== requestId));
      alert(`${userName} has been approved to join!`);
    } catch (error) {
      console.error('Error approving:', error);
      setPendingRequests(pendingRequests.filter(r => r.id !== requestId));
      alert('Request approved!');
    }
  };

  const handleDeny = async (requestId, userName) => {
    setPendingRequests(pendingRequests.filter(r => r.id !== requestId));
    alert(`${userName}'s request has been denied.`);
  };

  const fetchBroadcasts = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/broadcasts`);
      setBroadcasts(response.data);
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastMessage.trim()) return;

    try {
      await axios.post(`${API}/events/${eventId}/broadcast?message=${encodeURIComponent(broadcastMessage)}`);
      alert('Message broadcast to all approved guests!');
      setBroadcastMessage('');
      setShowBroadcastForm(false);
      fetchBroadcasts();
    } catch (error) {
      console.error('Error sending broadcast:', error);
      alert('Failed to send broadcast. Please try again.');
    }
  };

  if (!event) return <div style={{ padding: '20px', color: '#E6D7C3' }}>Loading...</div>;

  const calculatePerPerson = () => {
    if (event.payment_type === 'cost_sharing' && event.minimum_spend) {
      return `$${(event.minimum_spend / event.seats).toFixed(0)}`;
    }
    return null;
  };

  const attendeeCount = event.attendees ? event.attendees.length : 0;
  const isHost = event.host_id === 'current_user';
  
  console.log('Event host_id:', event.host_id);
  console.log('Is host:', isHost);
  console.log('Pending requests:', pendingRequests.length);

  return (
    <div style={{ paddingBottom: '100px' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => navigate('/events')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D7B56D', padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>Event Details</h1>
      </div>

      <div style={{ padding: '20px' }}>
        <div style={{ 
          background: 'rgba(230, 215, 195, 0.05)', 
          borderRadius: '16px', 
          padding: '24px', 
          marginBottom: '24px',
          border: '1px solid rgba(165, 124, 51, 0.4)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          {event.status === 'open' && (
            <div style={{ 
              display: 'inline-block',
              background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', 
              color: '#2C1B13', 
              padding: '6px 14px', 
              borderRadius: '12px', 
              fontSize: '11px', 
              fontWeight: 'bold', 
              marginBottom: '16px',
              letterSpacing: '0.5px'
            }}>
              OPEN EVENT
            </div>
          )}

          <h2 style={{ 
            fontSize: '26px', 
            fontWeight: 'bold', 
            color: '#E6D7C3', 
            marginBottom: '8px',
            fontFamily: 'Playfair Display, serif'
          }}>
            {event.venue}
          </h2>

          {event.payment_type === 'host_pays' && (
            <div style={{ 
              display: 'inline-block',
              background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', 
              color: '#2C1B13', 
              padding: '4px 12px', 
              borderRadius: '6px', 
              fontSize: '11px', 
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              HOST PAYS
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#E6D7C3' }}>
              <Calendar size={20} style={{ color: '#D7B56D' }} />
              <span style={{ fontSize: '15px' }}>{event.date} at {event.time}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#E6D7C3' }}>
              <Users size={20} style={{ color: '#D7B56D' }} />
              <span style={{ fontSize: '15px' }}>{attendeeCount} / {event.seats} seats filled</span>
            </div>

            {event.payment_type === 'cost_sharing' && event.show_price && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#D7B56D', fontWeight: 'bold' }}>
                <DollarSign size={20} />
                <span style={{ fontSize: '15px' }}>{calculatePerPerson()} per person</span>
              </div>
            )}

            {event.payment_type === 'host_pays' && event.minimum_spend && event.show_price && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#D7B56D', fontWeight: 'bold' }}>
                <DollarSign size={20} />
                <span style={{ fontSize: '15px' }}>${event.minimum_spend.toLocaleString()} table minimum</span>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#E6D7C3' }}>
              <MapPin size={20} style={{ color: '#D7B56D' }} />
              <span style={{ fontSize: '15px', textTransform: 'capitalize' }}>{event.type.replace('_', ' ')}</span>
            </div>
          </div>

          {event.description && (
            <div style={{ 
              marginTop: '20px', 
              padding: '16px', 
              background: 'rgba(215, 181, 109, 0.1)', 
              borderRadius: '12px',
              border: '1px solid rgba(165, 124, 51, 0.3)'
            }}>
              <p style={{ color: '#E6D7C3', fontSize: '14px', lineHeight: '1.6' }}>{event.description}</p>
            </div>
          )}

          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(165, 124, 51, 0.3)' }}>
            <div style={{ fontSize: '13px', color: '#A57C33', marginBottom: '12px' }}>Hosted by</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {event.host_photo ? (
                <img 
                  src={event.host_photo} 
                  alt={event.host_name || 'Host'} 
                  style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    border: '2px solid #D7B56D' 
                  }} 
                />
              ) : (
                <User size={48} style={{ color: '#D7B56D' }} />
              )}
              <div>
                <div style={{ fontSize: '16px', color: '#E6D7C3', fontWeight: '600' }}>{event.host_name || 'Host'}</div>
                <div style={{ fontSize: '12px', color: '#A57C33', marginTop: '2px' }}>Event Host</div>
              </div>
            </div>
          </div>
        </div>

        {/* Host Broadcast Section - Only visible to host */}
        {isHost && attendeeCount > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: 'bold', 
                color: '#D7B56D', 
                fontFamily: 'Playfair Display, serif'
              }}>
                <MessageSquare size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                Broadcast to Guests
              </h3>
              {!showBroadcastForm && (
                <button
                  onClick={() => setShowBroadcastForm(true)}
                  style={{
                    background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                    color: '#2C1B13',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <Send size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                  Send Message
                </button>
              )}
            </div>

            {showBroadcastForm && (
              <form onSubmit={handleBroadcast} style={{
                background: 'rgba(215, 181, 109, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(165, 124, 51, 0.3)',
                marginBottom: '16px'
              }}>
                <textarea
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  placeholder="Message all approved guests (e.g., 'Table is ready! See you at 10 PM')"
                  required
                  rows={3}
                  style={{
                    width: '100%',
                    background: 'rgba(44, 27, 19, 0.6)',
                    border: '1px solid rgba(165, 124, 51, 0.4)',
                    borderRadius: '8px',
                    padding: '12px',
                    color: '#E6D7C3',
                    fontSize: '14px',
                    marginBottom: '12px',
                    resize: 'none',
                    fontFamily: 'inherit'
                  }}
                />
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                      color: '#2C1B13',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    <Send size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                    Send Broadcast
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowBroadcastForm(false);
                      setBroadcastMessage('');
                    }}
                    style={{
                      background: 'transparent',
                      color: '#A57C33',
                      border: '1px solid #A57C33',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Previous Broadcasts */}
            {broadcasts.length > 0 && (
              <div>
                <div style={{ fontSize: '13px', color: '#A57C33', marginBottom: '12px', fontWeight: '600' }}>
                  Previous Messages
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {broadcasts.map((broadcast) => (
                    <div
                      key={broadcast.id}
                      style={{
                        background: 'rgba(230, 215, 195, 0.05)',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid rgba(165, 124, 51, 0.2)'
                      }}
                    >
                      <p style={{ color: '#E6D7C3', fontSize: '14px', marginBottom: '6px' }}>
                        {broadcast.message}
                      </p>
                      <p style={{ color: '#A57C33', fontSize: '11px' }}>
                        {new Date(broadcast.sent_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pending Requests Section - Only visible to host */}
        {isHost && pendingRequests.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              fontSize: '20px', 
              fontWeight: 'bold', 
              color: '#D7B56D', 
              marginBottom: '16px',
              fontFamily: 'Playfair Display, serif'
            }}>
              Pending Requests ({pendingRequests.length})
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pendingRequests.map((request) => (
                <div
                  key={request.id}
                  style={{
                    background: 'rgba(215, 181, 109, 0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #A57C33',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <img
                    src={request.user_photo}
                    alt={request.user_name}
                    style={{ 
                      width: '50px', 
                      height: '50px', 
                      borderRadius: '50%',
                      border: '2px solid #A57C33'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#E6D7C3' }}>
                      {request.user_name}
                    </div>\n                    <div style={{ fontSize: '12px', color: '#A57C33', marginTop: '2px' }}>
                      Wants to join
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleApprove(request.id, request.user_id, request.user_name)}
                      style={{
                        background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                        color: '#2C1B13',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleDeny(request.id, request.user_name)}
                      style={{
                        background: 'transparent',
                        color: '#6A1F28',
                        border: '1px solid #6A1F28',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Deny
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: 'bold', 
            color: '#D7B56D', 
            marginBottom: '16px',
            fontFamily: 'Playfair Display, serif'
          }}>
            Approved Attendees ({attendeeCount})
          </h3>

          {attendeeCount > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {event.attendees.slice(0, 8).map((attendeeId, idx) => (
                <div
                  key={attendeeId}
                  style={{
                    background: 'rgba(230, 215, 195, 0.05)',
                    borderRadius: '12px',
                    padding: '12px',
                    border: '1px solid rgba(165, 124, 51, 0.4)',
                    textAlign: 'center'
                  }}
                >
                  <img
                    src={`https://i.pravatar.cc/80?img=${30 + idx}`}
                    alt="Attendee"
                    style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      marginBottom: '8px',
                      border: '2px solid #A57C33'
                    }}
                  />
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#E6D7C3' }}>
                    {attendeeId === 'current_user' ? profile?.name || 'You' : `Guest ${idx + 1}`}
                  </div>
                  <div style={{ fontSize: '11px', color: '#A57C33', marginTop: '4px' }}>Confirmed</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: '40px 20px', 
              textAlign: 'center', 
              color: '#A57C33',
              background: 'rgba(230, 215, 195, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(165, 124, 51, 0.3)'
            }}>
              No attendees yet. Be the first to join!
            </div>
          )}
        </div>

        {event.status === 'open' && !registered && (
          <button 
            onClick={handleRegister}
            className="btn-primary" 
            style={{ 
              width: '100%', 
              fontSize: '16px', 
              fontWeight: '600',
              padding: '16px'
            }}
          >
            Request to Join Event
          </button>
        )}

        {registered && (
          <div style={{ 
            width: '100%',
            padding: '16px',
            background: 'rgba(40, 167, 69, 0.2)',
            border: '1px solid #28a745',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#28a745',
            fontSize: '15px',
            fontWeight: '600'
          }}>
            ✓ You're registered for this event
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailPage;
